
import React, { useState } from 'react';
import axios from 'axios';
import { Edit, Trash, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import TagDialog from './TagDialog';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface Tag {
  id: string;
  name: string;
}

interface TagsTabProps {
  tags: Tag[];
  fetchTags: () => Promise<void>;
}

const TagsTab: React.FC<TagsTabProps> = ({ tags, fetchTags }) => {
  const [currentTag, setCurrentTag] = useState<Tag | null>(null);
  const [isTagDialogOpen, setIsTagDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleOpenTagDialog = (tag?: Tag) => {
    if (tag) {
      setCurrentTag(tag);
    } else {
      setCurrentTag({ id: '', name: '' });
    }
    setIsTagDialogOpen(true);
  };

  const handleSaveTag = async (tagName: string) => {
    if (!currentTag) return;
    
    try {
      setIsSubmitting(true);
      const tagData = { name: tagName };
      
      // Make sure headers are set for all requests
      const config = {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      };
      
      if (!currentTag.id) {
        // Create new tag
        await axios.post('/api/tags', tagData, config);
        toast({
          title: "Tag added",
          description: `${tagName} has been added successfully.`,
        });
      } else {
        // Update existing tag
        await axios.put(`/api/tags/${currentTag.id}`, tagData, config);
        toast({
          title: "Tag updated",
          description: `${tagName} has been updated successfully.`,
        });
      }
      
      await fetchTags();
      setIsTagDialogOpen(false);
    } catch (error: any) {
      console.error('Error saving tag:', error);
      
      // Enhanced error message
      let errorMessage = "There was a problem saving the tag.";
      
      if (error.response) {
        if (error.response.status === 409) {
          errorMessage = "A tag with this name already exists.";
        } else if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      }
      
      toast({
        title: "Error saving tag",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const openDeleteDialog = (tag: Tag) => {
    setCurrentTag(tag);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteTag = async () => {
    if (!currentTag) return;
    
    try {
      setIsSubmitting(true);
      // Make sure headers are set for delete request
      const config = {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      };
      
      await axios.delete(`/api/tags/${currentTag.id}`, config);
      toast({
        title: "Tag deleted",
        description: `${currentTag.name} has been deleted.`,
      });
      await fetchTags();
      setIsDeleteDialogOpen(false);
    } catch (error) {
      console.error('Error deleting tag:', error);
      toast({
        title: "Error deleting tag",
        description: "There was a problem deleting the tag.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Tag Management</h1>
        <Button onClick={() => handleOpenTagDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          Add Tag
        </Button>
      </div>
      
      <div className="bg-white dark:bg-gray-800 rounded-md shadow">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="text-right w-32">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tags.length === 0 ? (
              <TableRow>
                <TableCell colSpan={2} className="text-center py-8">
                  No tags found.
                </TableCell>
              </TableRow>
            ) : (
              tags.map((tag) => (
                <TableRow key={tag.id}>
                  <TableCell className="font-medium">{tag.name}</TableCell>
                  <TableCell className="text-right">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => handleOpenTagDialog(tag)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => openDeleteDialog(tag)}
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <TagDialog
        isOpen={isTagDialogOpen}
        setIsOpen={setIsTagDialogOpen}
        tagName={currentTag?.name || ''}
        onSave={handleSaveTag}
      />

      <DeleteConfirmationDialog
        isOpen={isDeleteDialogOpen}
        setIsOpen={setIsDeleteDialogOpen}
        title="Confirm Delete"
        message={`Are you sure you want to delete "${currentTag?.name}"? This action cannot be undone.`}
        onDelete={handleDeleteTag}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default TagsTab;
